import { useSaudiListDateRange } from '../../products/saudi/hooks/useSaudiListDateRange';
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  GitCompare, Plus, Search, X, Eye, Filter,
  ChevronDown, ChevronUp, Download, Upload, FileText, FileSpreadsheet,
  FileOutput, List, LayoutList, LayoutGrid, RefreshCcw,
  CheckCircle, Calendar, Hash, Users, Package, DollarSign,
  Star, Award, Tag, XCircle, Clock, AlertCircle, Trash2
} from 'lucide-react';
import QawaemErpRowActions from '../../products/saudi/components/QawaemErpRowActions';
import QawaemClickableStatusBadge from '../../products/saudi/components/QawaemClickableStatusBadge';
import TableColumnHeaderFilter from '../../shared/components/table/TableColumnHeaderFilter';
import { useColumnHeaderFilters } from '../../shared/hooks/useColumnHeaderFilters';
import { applyColumnFilters, textColumnRule, exactColumnRule } from '../../shared/utils/columnHeaderFilters';
import localApiAdapter from '../../core/services/localApiAdapter';
import { exportToCSV, exportToExcel, triggerFileInput, importFromFile } from '../../shared/utils/importExportUtils';
import FacturationListPageLayout, { facturationTableScrollClass } from '../facturation/components/FacturationListPageLayout';
import FacturationViewModeToggle from '../facturation/components/FacturationViewModeToggle';
import { wrapControlsStripSearch } from '../../shared/utils/listPageSearchVisibility';
import QawaemTableHeaderActionsCell from '../../products/saudi/components/QawaemTableHeaderActionsCell';
import QawaemTableCheckbox from '../../products/saudi/components/QawaemTableCheckbox';
import RequiredLabelText from '../../shared/components/custom/RequiredLabelText';
import { useAchatsConfig } from './useAchatsConfig';
import QawaemErpCrudModal, { QawaemErpViewModal } from '../../products/saudi/components/QawaemErpCrudModal';
import { isSaudiBrand } from '../../products/saudi/utils/isSaudiBrand';
import { useGlobalTheme } from '../../shared/hooks/useGlobalTheme';
import { CustomSelect, CustomNumberInput, CustomDatePicker } from '../../shared/components/custom';
import { getErpOverviewGridAttrs } from '../../shared/utils/erpOverviewGridAttrs';
import { formatBrandMoney } from '../../shared/utils/brandCurrency';
import { useTranslation } from 'react-i18next';
import { mergeStickySelectClass } from '../../products/saudi/components/QawaemTableStickyActions';
import {
  buildAchatsColumnDefs,
  buildAchatsImportExportProps,
  achatsColHeaderProps,
  ACHATS_FORM_MODAL_SHELL,
  ACHATS_FORM_TAB_PANEL,
  ACHATS_FORM_GRID,
  useAchatsModalTheme,
  useAchatsColumnVisibility,
  ACHATS_TABLE_ACTIONS_TH_CLASS,
  useAchatsRowSelection,
  buildAchatsBulkDeleteFilterAction,
  AchatsBulkDeleteBar,
  useAppNotificationToast,
  AchatsCrudNotificationToast,
  sortRecordsNewestFirst,
  notifyCreated,
  notifyUpdated,
  notifyDeleted,
  notifyBulkDeleted,
  notifyError,
  resolveAchatsEntityReference,
  AchatsConfigNotice,
  useAchatsListViewMode,
  useAchatsListDisplay,
  achatsSettingsFromConfig,
  fetchAchatsSuppliers,
  fetchAchatsProducts,
  fetchAchatsCatalog,
  findAchatsCatalogItem,
  buildAchatsProductSelectOptions,
  countAchatsLowStockCatalog,
  isAchatsCatalogLowStock,
} from './achatsListPageUtils';

const NS = 'modules.achats.pages.comparatifOffresPage';
const API_BASE = '/achats/comparatif-offres';

const EMPTY_LINE = {
  product_id: '',
  supplier_id: '',
  supplier_name: '',
  product_name: '',
  product_code: '',
  quantity: '',
  unit_price: '',
  delivery_days: '',
  payment_terms: '',
  warranty_months: '',
  score: '',
  is_selected: false,
  notes: '',
};

const EMPTY_FORM = {
  comparison_number: '',
  title: '',
  request_reference: '',
  comparison_date: '',
  status: '',
  selected_supplier_id: '',
  selected_supplier_name: '',
  total_estimated_budget: '',
  evaluation_criteria: '',
  notes: '',
};

const ComparatifOffresPage = () => {
  const { t } = useTranslation();
  const p = (key, opts) => t(`${NS}.${key}`, opts);
  const entityLabel = p('entity_label', { defaultValue: p('title') });
  const { toast: notifyToast, show: showNotify, dismiss: dismissNotify } = useAppNotificationToast();
  const qawaem = isSaudiBrand();
  const { isDashboardLightMode } = useGlobalTheme();
  const { cfgTheme, modal: m } = useAchatsModalTheme();
  const achatsConfig = useAchatsConfig();
  const achatsSettings = achatsSettingsFromConfig(achatsConfig);

  const STATUS_DEFS = useMemo(
    () => [
      { value: 'draft', labelKey: 'status_draft' },
      { value: 'in_progress', labelKey: 'status_in_progress' },
      { value: 'completed', labelKey: 'status_completed' },
      { value: 'cancelled', labelKey: 'status_cancelled' },
    ],
    []
  );

  const statusOptions = useMemo(
    () => [
      { value: 'draft', label: p('status_draft'), icon: FileText, style: 'bg-gray-500/20 text-gray-300 border-gray-500/30' },
      { value: 'in_progress', label: p('status_in_progress'), icon: GitCompare, style: 'bg-blue-500/20 text-blue-300 border-blue-500/30' },
      { value: 'completed', label: p('status_completed'), icon: CheckCircle, style: 'bg-green-500/20 text-green-300 border-green-500/30' },
      { value: 'cancelled', label: p('status_cancelled'), icon: XCircle, style: 'bg-red-500/20 text-red-300 border-red-500/30' },
    ],
    [p],
  );

  const [comparisons, setComparisons] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [availableProducts, setAvailableProducts] = useState([]);
  const [catalogItems, setCatalogItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [viewingRecord, setViewingRecord] = useState(null);
  const [viewMode, setViewMode] = useAchatsListViewMode(achatsConfig);
  const [searchTerm, setSearchTerm] = useState('');
  const { dateRange, setDateRange } = useSaudiListDateRange();
  const [statusFilter, setStatusFilter] = useState('all');
  const [lines, setLines] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [showAdvancedOptions, setShowAdvancedOptions] = useState(false);
  const [mainViewMode, setMainViewMode] = useState('all_comparisons');
  const [quickFilter, setQuickFilter] = useState('all');
  const { filters: colFilters, setFilter: setColFilter, editingKey, setEditingKey } = useColumnHeaderFilters();
  const [showImportDropdown, setShowImportDropdown] = useState(false);
  const [showExportDropdown, setShowExportDropdown] = useState(false);
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [isGeneratingExcel, setIsGeneratingExcel] = useState(false);
  const importDropdownRef = useRef(null);
  const exportDropdownRef = useRef(null);
  const [formData, setFormData] = useState({ ...EMPTY_FORM });

  const supplierOptions = useMemo(
    () =>
      suppliers.map((sup) => ({
        value: sup.id || sup.name,
        label: sup.company_name || sup.name || sup.id,
      })),
    [suppliers]
  );

  const productOptions = useMemo(
    () => buildAchatsProductSelectOptions(availableProducts, catalogItems, {
      lowStockAlertEnabled: achatsConfig.lowStockAlertEnabled,
    }),
    [availableProducts, catalogItems, achatsConfig.lowStockAlertEnabled],
  );

  const lowStockCatalogCount = useMemo(
    () => (achatsConfig.lowStockAlertEnabled ? countAchatsLowStockCatalog(catalogItems) : 0),
    [achatsConfig.lowStockAlertEnabled, catalogItems],
  );

  useEffect(() => {
    loadComparisons();
    loadSuppliers();
    loadProducts();
  }, [refreshTrigger]);

  useEffect(() => {
    if (!achatsConfig.lowStockAlertEnabled) {
      setCatalogItems([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const items = await fetchAchatsCatalog();
      if (!cancelled) setCatalogItems(items);
    })();
    return () => { cancelled = true; };
  }, [achatsConfig.lowStockAlertEnabled]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (importDropdownRef.current && !importDropdownRef.current.contains(event.target)) {
        setShowImportDropdown(false);
      }
      if (exportDropdownRef.current && !exportDropdownRef.current.contains(event.target)) {
        setShowExportDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadComparisons = async () => {
    try {
      setLoading(true);
      const response = await localApiAdapter.get(API_BASE);
      const data = response?.data ?? response;
      setComparisons(sortRecordsNewestFirst(Array.isArray(data) ? data : []));
    } catch (error) {
      console.error('Error loading comparisons:', error);
      setComparisons([]);
    } finally {
      setLoading(false);
    }
  };

  const loadSuppliers = async () => {
    try {
      setSuppliers(await fetchAchatsSuppliers());
    } catch (error) {
      console.error('Error loading suppliers:', error);
      setSuppliers([]);
    }
  };

  const loadProducts = async () => {
    try {
      const products = await fetchAchatsProducts();
      setAvailableProducts(products);
    } catch (error) {
      console.error('Error loading products:', error);
      setAvailableProducts([]);
    }
  };

  const getStatusLabel = (status) => {
    const def = STATUS_DEFS.find((item) => item.value === status);
    return def ? p(def.labelKey) : status || '—';
  };

  const getStatusBadge = (status) => {
    const config = statusOptions.find((s) => s.value === status) || statusOptions[0];
    const { label, icon: Icon, style } = config;
    return (
      <span className={`px-3 py-1.5 rounded-full text-xs font-medium flex items-center gap-1.5 border ${style}`}>
        {Icon ? <Icon size={14} /> : null}
        {label}
      </span>
    );
  };

  const handleStatusChange = async (recordId, newStatus) => {
    try {
      await localApiAdapter.put(`${API_BASE}/${recordId}`, { status: newStatus });
      await loadComparisons();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const addLine = () => setLines((prev) => [...prev, {
    ...EMPTY_LINE,
    delivery_days: achatsConfig.defaultLeadTimeDays ? String(achatsConfig.defaultLeadTimeDays) : '',
  }]);
  const removeLine = (index) => setLines((prev) => prev.filter((_, i) => i !== index));
  const updateLine = (index, patch) => setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));

  const handleLineSupplierSelect = (index, supplierId) => {
    const supplier = suppliers.find((sup) => String(sup.id) === String(supplierId) || String(sup.name) === String(supplierId));
    updateLine(index, {
      supplier_id: supplier?.id || supplierId || '',
      supplier_name: supplier?.company_name || supplier?.name || supplierId || '',
    });
  };

  const handleLineProductSelect = (index, productId) => {
    if (!productId) {
      updateLine(index, { product_id: '', product_name: '', product_code: '', unit_price: '' });
      return;
    }
    const option = productOptions.find((opt) => String(opt.value) === String(productId));
    const product = option?.product || availableProducts.find((item) => String(item.id) === String(productId));
    if (!product) return;
    const patch = mapAchatsProductSelection(product, catalogItems, {
      lowStockAlertEnabled: achatsConfig.lowStockAlertEnabled,
    });
    updateLine(index, {
      product_id: patch.product_id,
      product_name: patch.product_name,
      product_code: patch.product_code,
      unit_price: patch.unit_price || '',
      supplier_id: option?.catalogItem?.supplier_id?.toString() || '',
      supplier_name: option?.catalogItem?.supplier_name || '',
    });
  };

  const getLineCatalogItem = (line) => findAchatsCatalogItem(catalogItems, {
    productId: line?.product_id,
    productCode: line?.product_code,
    productName: line?.product_name,
  });

  const resetForm = () => {
    setFormData({ ...EMPTY_FORM });
    setLines([]);
    setSelectedRecord(null);
    setShowModal(false);
  };

  const generateComparisonNumber = useCallback(
    () => resolveAchatsEntityReference(achatsSettings, 'quoteComparison', 'QCO', comparisons, (c) => c.comparison_number),
    [achatsSettings, comparisons],
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.status.trim()) {
      window.alert(p('alert_required'));
      return;
    }
    try {
      setLoading(true);
      const comparisonNumber = resolveAchatsEntityReference(
        achatsSettings,
        'quoteComparison',
        'QCO',
        comparisons,
        (c) => c.comparison_number,
        formData.comparison_number,
      );
      const payload = {
        comparison_number: comparisonNumber,
        title: formData.title.trim(),
        request_reference: formData.request_reference || null,
        comparison_date: formData.comparison_date || null,
        status: formData.status,
        selected_supplier_id: formData.selected_supplier_id || null,
        selected_supplier_name: formData.selected_supplier_name || null,
        total_estimated_budget: formData.total_estimated_budget !== '' ? parseFloat(formData.total_estimated_budget) : 0,
        evaluation_criteria: formData.evaluation_criteria || null,
        notes: formData.notes || null,
        lines: lines
          .filter((line) => line.supplier_name)
          .map((line) => ({
            supplier_id: line.supplier_id || null,
            supplier_name: line.supplier_name,
            product_name: line.product_name || null,
            product_code: line.product_code || null,
            quantity: parseFloat(line.quantity) || 0,
            unit_price: parseFloat(line.unit_price) || 0,
            delivery_days: parseInt(line.delivery_days, 10) || 0,
            payment_terms: line.payment_terms || null,
            warranty_months: parseInt(line.warranty_months, 10) || 0,
            score: parseFloat(line.score) || 0,
            is_selected: !!line.is_selected,
            notes: line.notes || null,
          })),
      };
      if (selectedRecord) {
        await localApiAdapter.put(`${API_BASE}/${selectedRecord.id}`, payload);
        notifyUpdated(showNotify, entityLabel);
      } else {
        await localApiAdapter.post(API_BASE, payload);
        notifyCreated(showNotify, entityLabel);
      }
      resetForm();
      setRefreshTrigger((prev) => prev + 1);
    } catch (error) {
      console.error('Error saving comparison:', error);
      notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setFormData({
      comparison_number: record.comparison_number ?? '',
      title: record.title ?? '',
      request_reference: record.request_reference ?? '',
      comparison_date: record.comparison_date ? String(record.comparison_date).split('T')[0] : '',
      status: record.status ?? '',
      selected_supplier_id: record.selected_supplier_id ?? '',
      selected_supplier_name: record.selected_supplier_name ?? '',
      total_estimated_budget: record.total_estimated_budget != null ? String(record.total_estimated_budget) : '',
      evaluation_criteria: record.evaluation_criteria ?? '',
      notes: record.notes ?? '',
    });
    setLines(
      Array.isArray(record.lines) && record.lines.length > 0
        ? record.lines.map((line) => ({
            product_id: line.product_id ?? '',
            supplier_id: line.supplier_id ?? '',
            supplier_name: line.supplier_name ?? '',
            product_name: line.product_name ?? '',
            product_code: line.product_code ?? '',
            quantity: line.quantity != null ? String(line.quantity) : '',
            unit_price: line.unit_price != null ? String(line.unit_price) : '',
            delivery_days: line.delivery_days != null ? String(line.delivery_days) : '',
            payment_terms: line.payment_terms ?? '',
            warranty_months: line.warranty_months != null ? String(line.warranty_months) : '',
            score: line.score != null ? String(line.score) : '',
            is_selected: line.is_selected === 1 || line.is_selected === true,
            notes: line.notes ?? '',
          }))
        : []
    );
    setFormTab('header');
    setShowModal(true);
  };

  const handleView = (record) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm(p('delete_confirm'))) {
      try {
        setLoading(true);
        await localApiAdapter.delete(`${API_BASE}/${id}`);
        notifyDeleted(showNotify, entityLabel);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        notifyError(showNotify, error?.response?.data?.error || p('alert_save_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleBulkDelete = async () => {
    if (!selectedRecords.length) return;
    if (window.confirm(p('bulk_delete_confirm', { count: selectedRecords.length }))) {
      try {
        setLoading(true);
        const deleteCount = selectedRecords.length;
        await Promise.all(selectedRecords.map((id) => localApiAdapter.delete(`${API_BASE}/${id}`)));
        notifyBulkDeleted(showNotify, deleteCount, entityLabel);
        setSelectedRecords([]);
        setRefreshTrigger((prev) => prev + 1);
      } catch (error) {
        notifyError(showNotify, p('alert_save_error'));
      } finally {
        setLoading(false);
      }
    }
  };

  const handleSelectRecord = (id) => {
    setSelectedRecords((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const stats = useMemo(() => ({
    total: comparisons.length,
    draft: comparisons.filter((c) => c.status === 'draft').length,
    in_progress: comparisons.filter((c) => c.status === 'in_progress').length,
    completed: comparisons.filter((c) => c.status === 'completed').length,
    cancelled: comparisons.filter((c) => c.status === 'cancelled').length,
  }), [comparisons]);

  const baseFilteredComparisons = useMemo(() => {
    let list = [...comparisons];
    if (dateRange.start || dateRange.end) {
      list = list.filter((c) => {
        if (!c.comparison_date) return false;
        const rowDate = new Date(c.comparison_date);
        if (dateRange.start) {
          const start = new Date(dateRange.start);
          start.setHours(0, 0, 0, 0);
          if (rowDate < start) return false;
        }
        if (dateRange.end) {
          const end = new Date(dateRange.end);
          end.setHours(23, 59, 59, 999);
          if (rowDate > end) return false;
        }
        return true;
      });
    }
    if (qawaem) {
      if (statusFilter !== 'all') list = list.filter((c) => c.status === statusFilter);
    } else if (quickFilter !== 'all') {
      list = list.filter((c) => c.status === quickFilter);
    }
    if (searchTerm.trim()) {
      const term = searchTerm.toLowerCase();
      list = list.filter(
        (c) =>
          c.comparison_number?.toLowerCase().includes(term) ||
          c.title?.toLowerCase().includes(term) ||
          c.request_reference?.toLowerCase().includes(term) ||
          c.selected_supplier_name?.toLowerCase().includes(term)
      );
    }
    return list;
  }, [comparisons, dateRange, qawaem, statusFilter, quickFilter, searchTerm]);

  const columnFilterRules = useMemo(
    () => [
      textColumnRule('comparison_number'),
      textColumnRule('title'),
      textColumnRule('request_reference'),
      textColumnRule('comparison_date', (c) => (c.comparison_date ? String(c.comparison_date).split('T')[0] : '')),
      textColumnRule('selected_supplier_name'),
      textColumnRule('lines_count', (c) => String(Array.isArray(c.lines) ? c.lines.length : 0)),
      exactColumnRule('status', (c) => c.status || ''),
    ],
    [],
  );

  const { filtered: filteredComparisons, displayed: displayedComparisons } = useAchatsListDisplay(
    baseFilteredComparisons,
    colFilters,
    columnFilterRules,
    achatsConfig.itemsPerPage,
  );

  const rowSelection = useAchatsRowSelection(selectedRecords, filteredComparisons);


  const handleSelectAll = () => {
    if (rowSelection.allRowsSelected) {
      setSelectedRecords([]);
    } else {
      setSelectedRecords(filteredComparisons.map((r) => r.id));
    }
  };

  const colHeader = (columnKey, label, opts = {}) =>
    achatsColHeaderProps({ columnKey, label, colFilters, setColFilter, editingKey, setEditingKey, ...opts });

  const quickFilterButtons = [
    { id: 'all', name: p('filter_all'), icon: LayoutGrid },
    { id: 'draft', name: p('status_draft'), icon: FileText },
    { id: 'in_progress', name: p('status_in_progress'), icon: GitCompare },
    { id: 'completed', name: p('status_completed'), icon: CheckCircle },
    { id: 'cancelled', name: p('status_cancelled'), icon: X },
  ];

  const distributionData = STATUS_DEFS.map(({ value, labelKey }) => ({
    name: p(labelKey),
    count: comparisons.filter((c) => c.status === value).length,
    color: 'bg-gradient-to-r from-blue-500 to-blue-600',
  }));

  const exportHeaders = useMemo(() => ([
    { key: 'comparison_number', label: p('col_number') },
    { key: 'title', label: p('col_title') },
    { key: 'request_reference', label: p('col_request') },
    { key: 'comparison_date', label: p('col_date') },
    { key: 'selected_supplier_name', label: p('col_winner') },
    { key: 'status', label: t('common.status') },
  ]), [p, t]);

  const handleExport = () => {
    const fileName = `comparatif_offres_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToCSV(filteredComparisons, exportHeaders, `${fileName}.csv`);
      setShowExportDropdown(false);
    } catch (error) {
      console.error('Comparative export error:', error);
    }
  };

  const handleExportExcel = () => {
    const fileName = `comparatif_offres_${new Date().toISOString().split('T')[0]}`;
    try {
      exportToExcel(filteredComparisons, exportHeaders, `${fileName}.xlsx`);
      setShowExportDropdown(false);
    } catch (error) {
      console.error('Comparative export error:', error);
    } finally {
      setIsGeneratingExcel(false);
    }
  };

  const handleImport = () => {
    triggerFileInput(async (file) => {
      try {
        const result = await importFromFile(file, exportHeaders, {
          requiredFields: ['title', 'status'],
        });
        if (result.success) {
          let successCount = 0;
          for (const record of result.data) {
            try {
              await localApiAdapter.post(API_BASE, record);
              successCount++;
            } catch (err) {
              console.error('Import record error:', err);
            }
          }
          setRefreshTrigger((prev) => prev + 1);
          setShowImportDropdown(false);
          window.alert(p('import_success', { total: result.totalCount, imported: successCount, defaultValue: `${successCount}/${result.totalCount}` }));
        }
      } catch (error) {
        console.error('Import error:', error);
      }
    }, '.csv,.xlsx,.xls');
  };

  const handleExportPDF = async () => {
    setIsGeneratingPDF(true);
    try {
      handleExport();
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  const comparatifColumnKeys = useMemo(
    () => ['comparison_number', 'title', 'request_reference', 'comparison_date', 'selected_supplier_name', 'lines', 'status', 'actions'],
    [],
  );
  const { columnVisibility, handleToggleColumn, isColumnVisible } = useAchatsColumnVisibility(comparatifColumnKeys);

  const qawaemColumns = useMemo(
    () => buildAchatsColumnDefs([
      { key: 'comparison_number', labelKey: 'col_number', required: true },
      { key: 'title', labelKey: 'col_title' },
      { key: 'request_reference', labelKey: 'col_request' },
      { key: 'comparison_date', labelKey: 'col_date' },
      { key: 'selected_supplier_name', labelKey: 'col_winner' },
      { key: 'lines', labelKey: 'col_lines' },
      { key: 'status', label: t('common.status') },
      { key: 'actions', label: t('common.actions'), required: true },
    ], p, columnVisibility),
    [p, t, columnVisibility],
  );
  const qawaemVisibleColumnCount = qawaemColumns.filter((c) => c.checked !== false).length;
  const tableColSpan = achatsTableColSpan(qawaemColumns);

  const listStats = useMemo(
    () => mapAchatsStatCards([
      { key: 'total', label: p('stat_total'), value: stats.total, icon: GitCompare, variant: 'copper' },
      { key: 'draft', label: p('status_draft'), value: stats.draft, icon: FileText, variant: 'forest' },
      { key: 'in_progress', label: p('status_in_progress'), value: stats.in_progress, icon: GitCompare, variant: 'copper' },
      achatsConfig.lowStockAlertEnabled && lowStockCatalogCount > 0
        ? { key: 'low_stock', label: p('stat_low_stock', { defaultValue: 'مخزون منخفض' }), value: lowStockCatalogCount, icon: AlertCircle, variant: 'copper' }
        : { key: 'completed', label: p('status_completed'), value: stats.completed, icon: CheckCircle, variant: 'forest' },
    ]),
    [stats, p, achatsConfig.lowStockAlertEnabled, lowStockCatalogCount],
  );

  const importExportProps = useMemo(
    () => buildAchatsImportExportProps({
      showImportDropdown,
      setShowImportDropdown,
      showExportDropdown,
      setShowExportDropdown,
      importDropdownRef,
      exportDropdownRef,
      importLabel: t('common.import'),
      exportLabel: t('common.export'),
      generatingLabel: t('common.loading'),
      isGeneratingPDF,
      handlers: {
        onImport: handleImport,
        onExportCSV: handleExport,
        onExportExcel: handleExportExcel,
        onExportPDF: handleExportPDF,
      },
    }),
    [showImportDropdown, showExportDropdown, isGeneratingPDF, t],
  );

  const advancedOptionsContent = (
    <div className="space-y-6 pt-6">
      <div className="bg-white/10 backdrop-blur-lg rounded-3xl p-2 border border-white/20">
        <div className="flex space-x-1 overflow-x-auto table-scroll">
          {quickFilterButtons.map((filter) => (
            <button
              key={filter.id}
              onClick={() => setQuickFilter(filter.id)}
              className={`flex-1 flex items-center justify-center space-x-2 px-4 py-3 rounded-2xl font-medium transition-all ${quickFilter === filter.id ? 'bg-gradient-to-r from-blue-500 to-blue-700 text-white shadow-lg' : 'text-white/80 hover:bg-white/10'}`}
            >
              <filter.icon className="h-5 w-5" />
              <span className="hidden sm:inline whitespace-nowrap">{filter.name}</span>
            </button>
          ))}
        </div>
      </div>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="bg-white/10 backdrop-blur-lg rounded-3xl p-6 border border-white/20">
        <h3 className="text-xl font-semibold text-white mb-4">{p('dist_overview')}</h3>
        <div {...getErpOverviewGridAttrs(distributionData.length, '3')}>
          {distributionData.map((item, index) => (
            <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium text-white">{item.name}</h4>
                <div className={`text-sm rounded-full px-2 py-0.5 ${item.color} text-white force-white`}>{item.count}</div>
              </div>
              <div className="w-full bg-white/10 h-3 rounded-full">
                <div className={`h-3 rounded-full ${item.color}`} style={{ width: `${stats.total > 0 ? (item.count / stats.total) * 100 : 0}%` }} />
              </div>
            </div>
          ))}
        </div>
      </motion.div>
    </div>
  );

  return (
    <>
      <AchatsConfigNotice
        message={!achatsConfig.enableQuoteComparison ? p('notice_comparison_disabled', { defaultValue: 'مقارنة العروض معطّلة — فعّلها من إعدادات المشتريات ← المناقصات والعروض.' }) : null}
      />
      <AchatsConfigNotice
        message={achatsConfig.lowStockAlertEnabled && lowStockCatalogCount > 0
          ? p('notice_low_stock_catalog', {
              defaultValue: '{{count}} منتج في فهرس الموردين تحت حد إعادة الطلب.',
              count: lowStockCatalogCount,
            })
          : null}
      />
      <FacturationListPageLayout
        title={p('title')}
        subtitle={p('subtitle')}
        icon={GitCompare}
        stats={listStats}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        filterActions={buildAchatsBulkDeleteFilterAction(selectedRecords.length, handleBulkDelete, { deleteLabel: `${t('common.delete')} (${selectedRecords.length})`, title: p('bulk_delete_confirm', { count: selectedRecords.length }) })}
        searchTerm={searchTerm}
        onSearchChange={setSearchTerm}
        searchPlaceholder={p('search_placeholder', { defaultValue: t('common.search') })}
        onCreate={() => {
          if (!achatsConfig.enableQuoteComparison) {
            alert(p('feature_disabled', { defaultValue: 'مقارنة العروض معطّلة من إعدادات المشتريات.' }));
            return;
          }
          setSelectedRecord(null);
          setLines([]);
          setFormData({
            ...EMPTY_FORM,
            comparison_number: generateComparisonNumber(),
            status: 'draft',
            comparison_date: new Date().toISOString().split('T')[0],
          });
          setFormTab('header');
          setShowModal(true);
        }}
        createLabel={p('new_item')}
        importExport={importExportProps}
        showAdvancedOptions={showAdvancedOptions}
        onToggleAdvancedOptions={() => setShowAdvancedOptions(!showAdvancedOptions)}
        advancedOptionsLabel={t('common.advanced_options')}
        advancedOptionsContent={advancedOptionsContent}
        controlsStrip={
          !qawaem ? (
            <div className="facturation-list-controls-strip p-4 border-b border-white/10">
              <div className="facturation-list-controls-row flex min-w-0 flex-row flex-wrap items-center gap-2 lg:gap-4">
                {wrapControlsStripSearch(viewMode, (
                  <div className="relative min-w-0 flex-1 max-w-md">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-white/50" />
                    <input
                      type="text"
                      placeholder={p('search_placeholder', { defaultValue: t('common.search') })}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                ))}
                <FacturationViewModeToggle
                  viewMode={viewMode}
                  onViewModeChange={setViewMode}
                  isLight={isDashboardLightMode}
                  cardsTitle={t('common.view_cards')}
                  tableTitle={t('common.view_table')}
                />
              </div>
            </div>
          ) : null
        }
      >
        {viewMode === 'table' && (
          <div className={facturationTableScrollClass(qawaem)}>
            <table className="w-full employees-table data-table">
              <thead className="glass-table-head">
                <tr>
                  <th className={mergeStickySelectClass('px-3 py-3 text-center align-middle')}>
                    <div className="flex justify-center">
                      <QawaemTableCheckbox
                        size="lg"
                        checked={rowSelection.allRowsSelected}
                        indeterminate={rowSelection.someRowsSelected}
                        onChange={handleSelectAll}
                      />
                    </div>
                  </th>
                  {isColumnVisible('comparison_number') ? <TableColumnHeaderFilter {...colHeader('comparison_number', p('col_number'))} /> : null}
                  {isColumnVisible('title') ? <TableColumnHeaderFilter {...colHeader('title', p('col_title'))} /> : null}
                  {isColumnVisible('request_reference') ? <TableColumnHeaderFilter {...colHeader('request_reference', p('col_request'))} /> : null}
                  {isColumnVisible('comparison_date') ? <TableColumnHeaderFilter {...colHeader('comparison_date', p('col_date'))} /> : null}
                  {isColumnVisible('selected_supplier_name') ? <TableColumnHeaderFilter {...colHeader('selected_supplier_name', p('col_winner'))} /> : null}
                  {isColumnVisible('lines') ? <TableColumnHeaderFilter {...colHeader('lines_count', p('col_lines'), { align: 'center' })} /> : null}
                  {isColumnVisible('status') ? <TableColumnHeaderFilter {...colHeader('status', t('common.status'), { kind: 'select', options: statusOptions, align: 'center' })} /> : null}
                  <QawaemTableHeaderActionsCell
                    thClassName={ACHATS_TABLE_ACTIONS_TH_CLASS}
                    label={t('common.actions')}
                    columns={qawaemColumns}
                    onToggle={handleToggleColumn}
                    visibleCount={qawaemVisibleColumnCount}
                    totalCount={qawaemColumns.length}
                  />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/10">
                {loading ? (
                  <tr><td colSpan={tableColSpan} className="text-center py-12 text-white/60">{t('common.loading')}</td></tr>
                ) : displayedComparisons.length > 0 ? (
                  displayedComparisons.map((record, index) => (
                    <tr key={record.id} className="hover:bg-white/5">
                      <td className={mergeStickySelectClass('px-3 py-4 text-center align-middle')}>
                          <div className="flex justify-center">
                            <QawaemTableCheckbox
                              checked={selectedRecords.includes(record.id)}
                              onChange={() => handleSelectRecord(record.id)}
                            />
                          </div>
                        </td>
                      {isColumnVisible('comparison_number') ? <td className="px-6 py-4 text-white font-mono font-semibold">{record.comparison_number}</td> : null}
                      {isColumnVisible('title') ? <td className="px-6 py-4 text-white">{record.title}</td> : null}
                      {isColumnVisible('request_reference') ? <td className="px-6 py-4 text-white/80">{record.request_reference || '—'}</td> : null}
                      {isColumnVisible('comparison_date') ? <td className="px-6 py-4 text-white/80">{record.comparison_date ? String(record.comparison_date).split('T')[0] : '—'}</td> : null}
                      {isColumnVisible('selected_supplier_name') ? <td className="px-6 py-4 text-white/80">{record.selected_supplier_name || '—'}</td> : null}
                      {isColumnVisible('lines') ? <td className="px-6 py-4 text-right text-white">{Array.isArray(record.lines) ? record.lines.length : 0}</td> : null}
                      {isColumnVisible('status') ? (
                      <td className="px-6 py-4 text-center">
                        <div className="flex justify-center">
                          <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                        </div>
                      </td>
                      ) : null}
                      <td className="px-6 py-4">
                        <QawaemErpRowActions
                          onView={() => handleView(record)}
                          onEdit={() => handleEdit(record)}
                          onDelete={() => handleDelete(record.id)}
                        />
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={tableColSpan} className="text-center py-12 text-white/60">
                      <GitCompare size={48} className="mx-auto mb-4 text-white/30" />
                      <p className="text-lg">{p('empty_title')}</p>
                      <p className="text-sm mt-2">{p('empty_hint')}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

        {viewMode === 'cards' && (
          <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
            {displayedComparisons.length > 0 ? displayedComparisons.map((record, index) => (
              <motion.div key={record.id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: index * 0.05 }} className="bg-white/[0.03] rounded-2xl p-6 border border-white/20 hover:border-white/40 hover:bg-white/[0.05] transition-all h-full min-w-0">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-white font-mono font-semibold">{record.comparison_number}</span>
                      <QawaemClickableStatusBadge status={record.status} recordId={record.id} options={statusOptions} onChange={handleStatusChange} title={p('click_change_status', { defaultValue: t('common.change_status', { defaultValue: 'تغيير الحالة' }) })} />
                    </div>
                    <p className="text-white font-semibold">{record.title}</p>
                    <p className="text-white/60 text-sm mt-1">{p('col_lines')}: {Array.isArray(record.lines) ? record.lines.length : 0}</p>
                  </div>
                  <QawaemErpRowActions
                    compact
                    size={14}
                    onView={() => handleView(record)}
                    onEdit={() => handleEdit(record)}
                    onDelete={() => handleDelete(record.id)}
                  />
                </div>
              </motion.div>
            )) : (
              <div className="text-center py-12 text-white/60"><GitCompare size={48} className="mx-auto mb-4 text-white/30" /><p>{p('empty_title')}</p></div>
            )}
          </div>
        )}
      </FacturationListPageLayout>

      <QawaemErpCrudModal
        open={showModal}
        onClose={() => resetForm()}
        title={selectedRecord ? p('modal_edit') : p('modal_new')}
        icon={GitCompare}
        formId="achats-crud-form"
        saveLabel={t('common.save')}
        onSubmit
        saving={loading}
        testId="achats-crud-modal"
      >
        <div className={ACHATS_FORM_MODAL_SHELL}>
          <div className={ACHATS_FORM_TAB_PANEL}>
            <form id="achats-crud-form" onSubmit={handleSubmit} className="space-y-6">
              <div className={ACHATS_FORM_GRID}>
                <div><label className={cfgTheme.label}>{p('field_number')}</label><input type="text" value={formData.comparison_number} onChange={(e) => setFormData({ ...formData, comparison_number: e.target.value })} className={`${cfgTheme.input} font-mono`} placeholder={p('placeholder_auto_generated', { defaultValue: 'يُولَّد تلقائياً' })} /></div>
                <div><label className={cfgTheme.label}><RequiredLabelText text={p('field_title')} required /></label><input type="text" required value={formData.title} onChange={(e) => setFormData({ ...formData, title: e.target.value })} className={cfgTheme.input} /></div>
                <div><label className={cfgTheme.label}>{p('field_request')}</label><input type="text" value={formData.request_reference} onChange={(e) => setFormData({ ...formData, request_reference: e.target.value })} className={cfgTheme.input} /></div>
                <div><label className={cfgTheme.label}>{p('field_date')}</label><CustomDatePicker value={formData.comparison_date} onChange={(val) => setFormData({ ...formData, comparison_date: val || '' })} /></div>
                <div><label className={cfgTheme.label}>{p('field_status')}</label><CustomSelect value={formData.status} onChange={(val) => setFormData({ ...formData, status: val || '' })} options={statusOptions} placeholder={p('field_status')} searchable creatable autoSelect={false} /></div>
                <div><label className={cfgTheme.label}>{p('field_budget')}</label><CustomNumberInput value={formData.total_estimated_budget} onChange={(val) => setFormData({ ...formData, total_estimated_budget: val })} placeholder="0" /></div>
                <div><label className={cfgTheme.label}>{p('field_winner')}</label><CustomSelect value={formData.selected_supplier_id} onChange={(val) => { const sup = suppliers.find((s) => String(s.id) === String(val)); setFormData({ ...formData, selected_supplier_id: val || '', selected_supplier_name: sup?.company_name || sup?.name || '' }); }} options={supplierOptions} placeholder={p('field_winner')} searchable creatable autoSelect={false} /></div>
                <div className="md:col-span-3"><label className={cfgTheme.label}>{p('field_criteria')}</label><textarea value={formData.evaluation_criteria} onChange={(e) => setFormData({ ...formData, evaluation_criteria: e.target.value })} rows={2} className={cfgTheme.input} /></div>
              </div>
              <div className={ACHATS_FORM_GRID}>
                {lines.map((line, index) => (
                  <div key={index} className="bg-white/5 rounded-xl p-4 border border-white/10 space-y-3 md:col-span-3">
                    <div className="flex justify-between items-center">
                      <span className="block w-full text-white/70 text-sm font-medium text-right">{p('line_label', { index: index + 1 })}</span>
                      <button type="button" onClick={() => removeLine(index)} className="text-red-400 hover:text-red-300"><Trash2 size={16} /></button>
                    </div>
                    <div className={ACHATS_FORM_GRID}>
                      <div><label className="block w-full text-white/60 text-xs mb-1 font-medium text-right">{p('field_supplier')}</label><CustomSelect value={line.supplier_id} onChange={(val) => handleLineSupplierSelect(index, val)} options={supplierOptions} placeholder={p('field_supplier')} searchable creatable autoSelect={false} /></div>
                      <div>
                        <label className="block w-full text-white/60 text-xs mb-1 font-medium text-right">{p('field_product')}</label>
                        <CustomSelect
                          value={line.product_id}
                          onChange={(val) => handleLineProductSelect(index, val)}
                          options={productOptions}
                          placeholder={p('field_product')}
                          searchable
                          autoSelect={false}
                        />
                        {achatsConfig.lowStockAlertEnabled && isAchatsCatalogLowStock(getLineCatalogItem(line)) ? (
                          <span className="text-amber-400 text-xs mt-1 block text-right">{p('badge_low_stock', { defaultValue: 'مخزون منخفض في فهرس المورد' })}</span>
                        ) : null}
                      </div>
                      <div><label className="block w-full text-white/60 text-xs mb-1 font-medium text-right">{p('field_qty')}</label><CustomNumberInput value={line.quantity} onChange={(val) => updateLine(index, { quantity: val })} placeholder="0" /></div>
                      <div><label className="block w-full text-white/60 text-xs mb-1 font-medium text-right">{p('field_unit_price')}</label><CustomNumberInput value={line.unit_price} onChange={(val) => updateLine(index, { unit_price: val })} placeholder="0" /></div>
                      <div><label className="block w-full text-white/60 text-xs mb-1 font-medium text-right">{p('field_delivery_days')}</label><CustomNumberInput value={line.delivery_days} onChange={(val) => updateLine(index, { delivery_days: val })} placeholder="0" /></div>
                      <div><label className="block w-full text-white/60 text-xs mb-1 font-medium text-right">{p('field_score')}</label><CustomNumberInput value={line.score} onChange={(val) => updateLine(index, { score: val })} placeholder="0" /></div>
                    </div>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={line.is_selected} onChange={(e) => updateLine(index, { is_selected: e.target.checked })} className="w-4 h-4 rounded border-white/20 bg-white/10 text-blue-500" />
                      <span className="text-white/80 text-sm flex items-center gap-1"><Star size={14} className="text-amber-400" />{p('field_selected')}</span>
                    </label>
                  </div>
                ))}
                <button type="button" onClick={addLine} className="md:col-span-3 w-full py-2 border border-dashed border-white/20 rounded-xl text-white/70 hover:text-white hover:border-white/40 transition-all flex items-center justify-center gap-2">
                  <Plus size={16} />{p('add_line')}
                </button>
              </div>
              <div className={ACHATS_FORM_GRID}>
                <div className="md:col-span-3"><label className={cfgTheme.label}>{p('section_notes')}</label><textarea value={formData.notes} onChange={(e) => setFormData({ ...formData, notes: e.target.value })} rows={3} className={cfgTheme.input} /></div>
              </div>
            </form>
          </div>
        </div>
      </QawaemErpCrudModal>

      {showViewModal && viewingRecord ? (
      <QawaemErpViewModal
        open
        onClose={() => setShowViewModal(false)}
        title={p('view_modal_title')}
        icon={Eye}
        testId="achats-view-modal"
      >
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_number')}</p><p className="text-white font-mono">{viewingRecord.comparison_number}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_date')}</p><p className="text-white">{viewingRecord.comparison_date ? String(viewingRecord.comparison_date).split('T')[0] : '—'}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{t('common.status')}</p>{getStatusBadge(viewingRecord.status)}</div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('col_winner')}</p><p className="text-white">{viewingRecord.selected_supplier_name || '—'}</p></div>
              <div><p className="text-white/60 text-xs uppercase mb-1">{p('field_budget')}</p><p className="text-white">{viewingRecord.total_estimated_budget ?? '—'}</p></div>
            </div>
            {Array.isArray(viewingRecord.lines) && viewingRecord.lines.length > 0 && (
              <div className="bg-white/5 rounded-xl p-4 border border-white/20 mb-4">
                <h3 className="text-lg font-semibold text-white mb-3">{p('section_lines')}</h3>
                <div className="space-y-2">
                  {viewingRecord.lines.map((line, idx) => (
                    <div key={idx} className="flex items-center justify-between bg-white/5 rounded-lg p-3 border border-white/10">
                      <div>
                        <p className={m.viewValue}>{line.supplier_name}</p>
                        <p className="text-white/60 text-sm">{line.product_name || '—'} · {line.quantity} × {line.unit_price}</p>
                      </div>
                      <div className="text-right">
                        {line.is_selected === 1 || line.is_selected === true ? <Star size={16} className="text-amber-400 inline" fill="currentColor" /> : null}
                        <p className="text-white/80 text-sm">{p('field_score')}: {line.score ?? 0}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {viewingRecord.notes && <div className={m.viewSection}><p className={`${cfgTheme.subtitle} text-sm`}>{viewingRecord.notes}</p></div>}
      </QawaemErpViewModal>
      ) : null}
      <AchatsCrudNotificationToast notifyToast={notifyToast} onDismiss={dismissNotify} />
    </>
  );
};

export default ComparatifOffresPage;
